import { useState } from 'react';

import { useToastStore } from '../../../stores/toastStore';
import type { PlatformProviderResponse } from '../api';
import { PlatformProviderForm } from '../components/PlatformProviderForm';
import {
  useCreatePlatformProvider,
  useDeletePlatformProvider,
  usePlatformProviders,
  useTestPlatformProvider,
  useUpdatePlatformProvider,
} from '../hooks/usePlatformProviders';

export function PlatformProvidersPage(): JSX.Element {
  const { data: providers, isLoading, isError } = usePlatformProviders();
  const pushToast = useToastStore((s) => s.push);

  const createMutation = useCreatePlatformProvider();
  const updateMutation = useUpdatePlatformProvider();
  const deleteMutation = useDeletePlatformProvider();
  const testMutation = useTestPlatformProvider();

  const [createOpen, setCreateOpen] = useState(false);
  const [editingProvider, setEditingProvider] = useState<PlatformProviderResponse | null>(null);

  return (
    <div className="space-y-6">
      <header className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Platform Providers</h1>
          <p className="mt-1 text-sm text-slate-500">Proveedores de IA compartidos de plataforma.</p>
        </div>
        <button type="button" className="btn-primary" onClick={() => setCreateOpen(true)}>Añadir provider</button>
      </header>

      {isError ? (
        <div className="card border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">No se pudo cargar la lista.</div>
      ) : null}

      <div className="card overflow-x-auto">
        <table className="w-full min-w-[720px] text-sm">
          <thead className="border-b border-slate-200 bg-slate-50">
            <tr>
              <th className="px-4 py-3 text-left font-medium text-slate-700">Nombre</th>
              <th className="px-4 py-3 text-left font-medium text-slate-700">Tipo</th>
              <th className="px-4 py-3 text-left font-medium text-slate-700">Modelo</th>
              <th className="px-4 py-3 text-left font-medium text-slate-700">Estado</th>
              <th className="px-4 py-3 text-left font-medium text-slate-700">Secret</th>
              <th className="px-4 py-3 text-right font-medium text-slate-700">Acciones</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {isLoading ? (
              Array.from({ length: 3 }).map((_, i) => (
                <tr key={i}>
                  {Array.from({ length: 6 }).map((__, j) => (
                    <td key={j} className="px-4 py-3"><div className="h-4 animate-pulse rounded bg-slate-100" /></td>
                  ))}
                </tr>
              ))
            ) : (
              (providers ?? []).map((p) => (
                <tr key={p.id} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-medium text-slate-900">{p.displayName}</td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-600">{p.providerType}</td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-600">{p.modelName}</td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${p.isActive ? 'bg-emerald-50 text-emerald-700' : 'bg-slate-100 text-slate-500'}`}>
                      {p.isActive ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-4 py-3 font-mono text-xs text-slate-500">{p.keyVaultSecretRef}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        type="button"
                        className="text-xs text-slate-600 hover:text-primary"
                        onClick={() => setEditingProvider(p)}
                      >
                        Editar
                      </button>
                      <button
                        type="button"
                        className="text-xs text-slate-600 hover:text-emerald-600"
                        disabled={testMutation.isPending}
                        onClick={() => {
                          testMutation.mutate(p.id, {
                            onSuccess: (res) => {
                              if (res.success) {
                                pushToast({ tone: 'success', title: 'Test OK', ...(res.latencyMs !== null ? { description: `${res.latencyMs}ms` } : {}) });
                              } else {
                                pushToast({ tone: 'error', title: 'Test fallido', ...(res.errorMessage !== null ? { description: res.errorMessage } : {}) });
                              }
                            },
                            onError: (err) => pushToast({ tone: 'error', title: 'Error de test', description: err.message }),
                          });
                        }}
                      >
                        Test
                      </button>
                      <button
                        type="button"
                        className="text-xs text-rose-600 hover:text-rose-800"
                        disabled={deleteMutation.isPending}
                        onClick={() => {
                          if (!confirm(`¿Eliminar "${p.displayName}"?`)) return;
                          deleteMutation.mutate(p.id, {
                            onSuccess: () => pushToast({ tone: 'success', title: 'Provider eliminado' }),
                            onError: (err) => pushToast({ tone: 'error', title: 'Error', description: err.message }),
                          });
                        }}
                      >
                        Eliminar
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {createOpen ? (
        <PlatformProviderForm
          mode="create"
          busy={createMutation.isPending}
          onClose={() => setCreateOpen(false)}
          onCreate={(req) => {
            createMutation.mutate(req, {
              onSuccess: () => { pushToast({ tone: 'success', title: 'Provider creado' }); setCreateOpen(false); },
              onError: (err) => pushToast({ tone: 'error', title: 'Error', description: err.message }),
            });
          }}
        />
      ) : null}

      {editingProvider !== null ? (
        <PlatformProviderForm
          mode="edit"
          provider={editingProvider}
          busy={updateMutation.isPending}
          onClose={() => setEditingProvider(null)}
          onUpdate={(req) => {
            updateMutation.mutate(
              { id: editingProvider.id, request: req },
              {
                onSuccess: () => { pushToast({ tone: 'success', title: 'Provider actualizado' }); setEditingProvider(null); },
                onError: (err) => pushToast({ tone: 'error', title: 'Error', description: err.message }),
              },
            );
          }}
        />
      ) : null}
    </div>
  );
}
